import { roundedPanelSx } from "../../overviewPanel";
import React, { useMemo } from "react";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import { ResponsivePie } from "@nivo/pie";
import ChartTooltipPortal from "./shared/ChartTooltipPortal";
import ChartTooltip from "./shared/ChartTooltip";
import { Theme } from "@mui/material/styles";
import { InstitutionalHolder } from "../hooks/useInstitutionalOwnership";
import { useStockCurrency } from "../../../../contexts/StockCurrencyContext";
import { formatNumber } from "../utils/formatNumber";

const getFlatUIColors = (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";

  return {
    background: isDark ? "#1a1a1a" : "#ffffff",
    surface: isDark ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.02)",
    border: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
    textPrimary: isDark ? "#ffffff" : "#1d1d1f",
    textSecondary: isDark ? "#9ca3af" : "#64748b",
    chartColors: [
      "#22c55e", // Primary green
      "#3b82f6", // Blue
      "#f59e0b", // Amber
      "#ef4444", // Red
      "#8b5cf6", // Purple
      "#06b6d4", // Cyan
      "#f97316", // Orange
      "#ec4899", // Pink
      "#84cc16", // Lime
      "#6366f1", // Indigo
    ],
  };
};

interface PieDataItem {
  id: string;
  label: string;
  value: number;
  marketValue: number;
  changeInOwnership: number;
}

interface InstitutionalOwnershipPieChartProps {
  holders: InstitutionalHolder[];
  quarterLabel: string;
}

const InstitutionalOwnershipPieChart: React.FC<InstitutionalOwnershipPieChartProps> = ({
  holders,
  quarterLabel,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const flatUIColors = getFlatUIColors(theme);
  const { currencySymbol } = useStockCurrency();

  const pieData = useMemo(() => {
    // Take top 10 holders for the pie chart
    const top10 = holders.slice(0, 10);

    let unknownCount = 0;
    return top10.map((holder): PieDataItem => {
      const hasName = holder.investorName?.trim();
      const name = hasName ? holder.investorName : `Unknown (${++unknownCount})`;
      return {
        id: name,
        label: name,
        value: holder.ownership,
        marketValue: holder.marketValue,
        changeInOwnership: holder.changeInOwnership,
      };
    });
  }, [holders]);

  const totalOwnership = useMemo(() => {
    return pieData.reduce((sum, item) => sum + item.value, 0);
  }, [pieData]);

  // Format market value using currencySymbol + formatNumber (financial data)
  const formatMarketValue = (value: number) => {
    return `${currencySymbol}${formatNumber(value)}`;
  };

  const formatPercentage = (value: number) => {
    return `${value?.toFixed(2) ?? "0.00"}%`;
  };

  const formatChangePercentage = (value: number) => {
    const sign = value > 0 ? "+" : "";
    return `${sign}${value?.toFixed(2) ?? "0.00"}%`;
  };

  if (pieData.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        ...roundedPanelSx(theme.palette.mode === "dark"),
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: flatUIColors.textPrimary,
          fontWeight: 600,
          mb: 1,
        }}
      >
        Top 10 Institutional Holders
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: flatUIColors.textSecondary,
          mb: 3,
        }}
      >
        Combined ownership: {formatPercentage(totalOwnership)} of outstanding shares ({quarterLabel})
      </Typography>

      <Box sx={{ height: isMobile ? 300 : 380 }}>
        <ResponsivePie
          data={pieData}
          animate={false}
          margin={isMobile
            ? { top: 20, right: 20, bottom: 20, left: 20 }
            : { top: 50, right: 120, bottom: 50, left: 120 }
          }
          innerRadius={0.5}
          padAngle={2}
          cornerRadius={0}
          activeOuterRadiusOffset={0}
          colors={flatUIColors.chartColors}
          borderWidth={0}
          enableArcLinkLabels={!isMobile}
          arcLinkLabelsSkipAngle={15}
          arcLinkLabelsTextColor={flatUIColors.textPrimary}
          arcLinkLabelsThickness={2}
          arcLinkLabelsColor={{ from: "color" }}
          arcLinkLabelsOffset={2}
          arcLinkLabelsDiagonalLength={16}
          arcLinkLabelsStraightLength={12}
          arcLinkLabel={(d) => {
            // Truncate long names for labels
            const name = d.data.label;
            return name.length > 15 ? `${name.substring(0, 15)}...` : name;
          }}
          arcLabelsSkipAngle={isMobile ? 15 : 22}
          arcLabelsTextColor={flatUIColors.background}
          valueFormat={(value) => formatPercentage(value)}
          tooltip={({ datum }) => (
            <ChartTooltipPortal>
              <ChartTooltip
                header={datum.data.label}
                headerColor={datum.color as string}
                rows={[
                  { label: 'Ownership', value: formatPercentage(datum.value) },
                  { label: 'Market Value', value: formatMarketValue(datum.data.marketValue) },
                  {
                    label: 'Change',
                    value: formatChangePercentage(datum.data.changeInOwnership),
                    color: datum.data.changeInOwnership > 0 ? '#22c55e' : datum.data.changeInOwnership < 0 ? '#ef4444' : undefined,
                  },
                ]}
              />
            </ChartTooltipPortal>
          )}
          theme={{
            background: "transparent",
            text: {
              fontSize: 12,
              fill: flatUIColors.textPrimary,
            },
            tooltip: {
              container: {
                background: "transparent",
                border: "none",
                borderRadius: 0,
                boxShadow: "none",
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default InstitutionalOwnershipPieChart;
